import { createElement, type CSSProperties, type SyntheticEvent } from "react";

import type {
  PatientWorkspaceOnboardingCommandRequestV1,
  PatientWorkspaceSummaryResponseV1,
} from "@amplecare/contracts";
import { designTokens } from "@amplecare/design-tokens";
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  type LayoutChangeEvent,
} from "react-native";

import { resolveCanonicalWorkspacePathname } from "../../config/runtime-config.js";
import { styles } from "../AppShell.styles.js";
import type { AppShellTheme } from "../shell-core.js";
import { resolvePatientBrand } from "./brand.js";

const brand = resolvePatientBrand();

export type PatientOnboardingCommandInput = Omit<
  PatientWorkspaceOnboardingCommandRequestV1,
  "schemaVersion" | "tenantId"
>;

type PatientLegacyOnboardingStepId =
  | "welcome"
  | "introduction"
  | "agent"
  | "navigation"
  | "summary"
  | "help";

export type PatientLegacyOnboardingCareType =
  | "chronic"
  | "post_op"
  | "pre_op";

export type PatientLegacyOnboardingFlowState = {
  careType: PatientLegacyOnboardingCareType;
  isActive: boolean;
  stepIndex: number;
};

export type PatientOnboardingRegionKey = "root" | "agent" | "nav" | "main";

export type PatientOnboardingLayoutRect = {
  height: number;
  width: number;
  x: number;
  y: number;
};

export type PatientOnboardingLayoutRects = Partial<
  Record<PatientOnboardingRegionKey, PatientOnboardingLayoutRect>
>;

const patientLegacyOnboardingSteps: readonly {
  id: PatientLegacyOnboardingStepId;
}[] = [
  { id: "welcome" },
  { id: "introduction" },
  { id: "agent" },
  { id: "navigation" },
  { id: "summary" },
  { id: "help" },
] as const;

export const patientLegacyOnboardingFinalStepIndex =
  patientLegacyOnboardingSteps.length - 1;

export const patientLegacyOnboardingDefaultCareType: PatientLegacyOnboardingCareType =
  "pre_op";

const patientLegacyOnboardingBaseVideoUrl = "/onboarding";

const patientLegacyOnboardingQueryParamNames = {
  careType: "care-type",
  disclaimer: "disclaimer",
  main: "onboarding",
  step: "step",
} as const;

function buildPatientOnboardingUrlWithCanonicalPathname(search: string): string {
  return `${resolveCanonicalWorkspacePathname(window.location.pathname)}${search}${window.location.hash}`;
}

export function clampPatientLegacyOnboardingStepIndex(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.min(
    patientLegacyOnboardingFinalStepIndex,
    Math.max(0, Math.trunc(value)),
  );
}

function isPatientLegacyOnboardingCareType(
  value: string | null,
): value is PatientLegacyOnboardingCareType {
  return value === "chronic" || value === "post_op" || value === "pre_op";
}

export function readPatientLegacyOnboardingFlowFromWindow(): PatientLegacyOnboardingFlowState | null {
  if (Platform.OS !== "web" || typeof window === "undefined") {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);

  if (
    searchParams.get(patientLegacyOnboardingQueryParamNames.main) !== "true"
  ) {
    return null;
  }

  const stepParam = searchParams.get(
    patientLegacyOnboardingQueryParamNames.step,
  );
  const careTypeParam = searchParams.get(
    patientLegacyOnboardingQueryParamNames.careType,
  );
  const parsedStepIndex = stepParam == null ? 0 : Number(stepParam);

  return {
    careType: isPatientLegacyOnboardingCareType(careTypeParam)
      ? careTypeParam
      : patientLegacyOnboardingDefaultCareType,
    isActive: true,
    stepIndex: clampPatientLegacyOnboardingStepIndex(parsedStepIndex),
  };
}

export function writePatientLegacyOnboardingFlowToWindow(
  flow: PatientLegacyOnboardingFlowState,
): void {
  if (Platform.OS !== "web" || typeof window === "undefined") {
    return;
  }

  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set(patientLegacyOnboardingQueryParamNames.main, "true");
  searchParams.set(
    patientLegacyOnboardingQueryParamNames.step,
    String(clampPatientLegacyOnboardingStepIndex(flow.stepIndex)),
  );
  searchParams.set(
    patientLegacyOnboardingQueryParamNames.careType,
    flow.careType,
  );
  searchParams.set(patientLegacyOnboardingQueryParamNames.disclaimer, "true");

  const nextSearch = searchParams.toString();
  window.history.replaceState(
    null,
    "",
    buildPatientOnboardingUrlWithCanonicalPathname(
      nextSearch ? `?${nextSearch}` : "",
    ),
  );
}

export function clearPatientLegacyOnboardingFlowFromWindow(): void {
  if (Platform.OS !== "web" || typeof window === "undefined") {
    return;
  }

  const searchParams = new URLSearchParams(window.location.search);
  searchParams.delete(patientLegacyOnboardingQueryParamNames.main);
  searchParams.delete(patientLegacyOnboardingQueryParamNames.step);
  searchParams.delete(patientLegacyOnboardingQueryParamNames.careType);
  searchParams.delete(patientLegacyOnboardingQueryParamNames.disclaimer);

  const nextSearch = searchParams.toString();
  window.history.replaceState(
    null,
    "",
    buildPatientOnboardingUrlWithCanonicalPathname(
      nextSearch ? `?${nextSearch}` : "",
    ),
  );
}

export function resolvePatientLegacyOnboardingCareType(
  summary: PatientWorkspaceSummaryResponseV1["patient"] | null,
): PatientLegacyOnboardingCareType {
  const haystack = [
    summary?.programLabel,
    summary?.statusLabel,
    summary?.procedureSummaryText,
    summary?.contactWindowText,
  ]
    .filter((part): part is string => typeof part === "string")
    .join(" ")
    .toLowerCase();

  if (haystack.includes("post")) {
    return "post_op";
  }

  if (haystack.includes("chronic")) {
    return "chronic";
  }

  return patientLegacyOnboardingDefaultCareType;
}

function resolvePatientLegacyOnboardingWelcomeVideoSource(
  careType: PatientLegacyOnboardingCareType,
): string {
  switch (careType) {
    case "post_op":
      return `${patientLegacyOnboardingBaseVideoUrl}/onboarding_welcome_marta_post_op.mp4`;
    case "pre_op":
      return `${patientLegacyOnboardingBaseVideoUrl}/onboarding_welcome_marta_pre_op.mp4`;
    default:
      return `${patientLegacyOnboardingBaseVideoUrl}/onboarding_welcome_marta_chronic.mp4`;
  }
}

function resolvePatientLegacyOnboardingStep(
  stepIndex: number,
): PatientLegacyOnboardingStepId {
  return (
    patientLegacyOnboardingSteps[
      clampPatientLegacyOnboardingStepIndex(stepIndex)
    ]?.id ?? "welcome"
  );
}

export function toPatientOnboardingLayoutRect(
  event: LayoutChangeEvent,
): PatientOnboardingLayoutRect {
  const { height, width, x, y } = event.nativeEvent.layout;

  return { height, width, x, y };
}

const patientLegacyOnboardingVideoStyle: CSSProperties = {
  borderRadius: 6,
  display: "block",
  height: "auto",
  maxWidth: "100%",
  objectFit: "cover",
  width: "min(50dvw, 640px)",
};

function renderPatientLegacyOnboardingVideo(input: {
  labelText: string;
  src: string;
}) {
  const { labelText, src } = input;

  if (Platform.OS !== "web") {
    return (
      <View
        style={[
          styles.patientOnboardingVideoFallback,
          {
            borderColor:
              designTokens.semanticTheme.clinicianWorkbench.borderSubtle,
            borderRadius: 6,
          },
        ]}
      >
        <Text style={styles.patientOnboardingBodyText}>{labelText}</Text>
      </View>
    );
  }

  return createElement("video", {
    autoPlay: true,
    controls: true,
    muted: false,
    onCanPlay: (event: SyntheticEvent<HTMLVideoElement>) => {
      const video = event.currentTarget;

      video.muted = false;

      const playResult = video.play();

      if (typeof playResult.catch === "function") {
        void playResult.catch(() => {
          // Browsers can block audible autoplay until the patient interacts.
        });
      }
    },
    playsInline: true,
    preload: "auto",
    src,
    style: patientLegacyOnboardingVideoStyle,
  });
}

function renderPatientLegacyOnboardingStepContent(input: {
  careType: PatientLegacyOnboardingCareType;
  isMediumLayout: boolean;
  layout: typeof designTokens.semanticLayout;
  stepId: PatientLegacyOnboardingStepId;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}) {
  const { careType, isMediumLayout, layout, stepId, theme, tokens } = input;
  const titleFontSize = isMediumLayout
    ? tokens.typography.fontSize["2xl"]
    : tokens.typography.fontSize.xl;
  const titleLineHeight = isMediumLayout
    ? tokens.typography.lineHeight["2xl"]
    : tokens.typography.lineHeight.xl;
  const titleStyle = [
    styles.patientOnboardingTitle,
    {
      color: theme.textPrimary,
      fontFamily: layout.displayFontFamily,
      fontSize: titleFontSize,
      lineHeight: titleLineHeight,
    },
  ];
  const bodyStyle = [
    styles.patientOnboardingBodyText,
    {
      color: theme.textSecondary,
      fontFamily: layout.bodyFontFamily,
      fontSize: tokens.typography.fontSize.md,
      lineHeight: tokens.typography.lineHeight.md,
    },
  ];

  if (stepId === "welcome") {
    return (
      <>
        <Text style={titleStyle}>Thank you for joining!</Text>
        <View
          style={[
            styles.patientOnboardingVideoSection,
            { gap: tokens.spacing.md },
          ]}
        >
          {renderPatientLegacyOnboardingVideo({
            labelText: `Welcome to ${brand.brandName}`,
            src: resolvePatientLegacyOnboardingWelcomeVideoSource(careType),
          })}
          <Text style={titleStyle}>
            {`Welcome to ${brand.brandName}`}
          </Text>
          <Text style={bodyStyle}>
            We're here to make sure you feel informed, supported, and at ease as
            you prepare. If you have any questions, just let us know. You're in
            good hands.
          </Text>
        </View>
      </>
    );
  }

  if (stepId === "introduction") {
    return (
      <>
        <Text style={titleStyle}>
          First, let's get familiar with your Dashboard
        </Text>
        <View
          style={[
            styles.patientOnboardingVideoSection,
            { gap: tokens.spacing.md },
          ]}
        >
          {renderPatientLegacyOnboardingVideo({
            labelText: "Dashboard introduction",
            src: `${patientLegacyOnboardingBaseVideoUrl}/onboarding_introduction_marta.mp4`,
          })}
          <Text style={bodyStyle}>
            It contains all relevant information and enables you to chat with me
            at any time
          </Text>
        </View>
      </>
    );
  }

  if (stepId === "agent") {
    return (
      <View
        style={[styles.patientOnboardingTextStep, { gap: tokens.spacing.sm }]}
      >
        <Text
          style={[
            styles.patientOnboardingArrow,
            {
              color: theme.textPrimary,
              fontFamily: layout.displayFontFamily,
            },
          ]}
        >
          ↑
        </Text>
        <Text style={titleStyle}>
          Use prompt window on top to access our chat
        </Text>
        <Text style={bodyStyle}>
          You can send me text or voice messages directly
        </Text>
      </View>
    );
  }

  if (stepId === "navigation") {
    return (
      <View
        style={[styles.patientOnboardingTextStep, { gap: tokens.spacing.sm }]}
      >
        {isMediumLayout ? (
          <Text
            style={[
              styles.patientOnboardingArrow,
              {
                color: theme.textPrimary,
                fontFamily: layout.displayFontFamily,
              },
            ]}
          >
            ↑
          </Text>
        ) : null}
        <Text style={titleStyle}>
          Use menu to access your relevant information
        </Text>
        <Text style={bodyStyle}>
          Select section for details on your appointments, prescribed
          medications and important documents
        </Text>
        {!isMediumLayout ? (
          <Text
            style={[
              styles.patientOnboardingArrow,
              {
                color: theme.textPrimary,
                fontFamily: layout.displayFontFamily,
              },
            ]}
          >
            ↓
          </Text>
        ) : null}
      </View>
    );
  }

  if (stepId === "summary") {
    return (
      <View
        style={[styles.patientOnboardingTextStep, { gap: tokens.spacing.sm }]}
      >
        <Text style={titleStyle}>
          Here's a summary of all important information
        </Text>
        <Text
          style={[
            styles.patientOnboardingArrow,
            {
              color: theme.textPrimary,
              fontFamily: layout.displayFontFamily,
            },
          ]}
        >
          ↓
        </Text>
      </View>
    );
  }

  return (
    <>
      <Text style={titleStyle}>I'm here for you 24/7!</Text>
      <View
        style={[
          styles.patientOnboardingVideoSection,
          { gap: tokens.spacing.md },
        ]}
      >
        {renderPatientLegacyOnboardingVideo({
          labelText: `${brand.brandName} help`,
          src: `${patientLegacyOnboardingBaseVideoUrl}/onboarding_help_marta.mp4`,
        })}
        <Text style={bodyStyle}>
          Questions? No worries, just open the chat and I'll be glad to help.
        </Text>
      </View>
    </>
  );
}

function resolvePatientLegacyOnboardingHighlightRect(input: {
  isMediumLayout: boolean;
  layoutRects: PatientOnboardingLayoutRects;
  stepId: PatientLegacyOnboardingStepId;
}): PatientOnboardingLayoutRect | null {
  const { isMediumLayout, layoutRects, stepId } = input;
  const rootRect = layoutRects.root;

  if (!rootRect || !isMediumLayout) {
    return null;
  }

  const horizontalPadding =
    rootRect.width < 960 ? 24 : rootRect.width < 1180 ? 40 : 72;
  const contentMaxWidth = Math.max(
    320,
    Math.min(1280, rootRect.width - horizontalPadding * 2),
  );
  const contentLeft = Math.max(0, (rootRect.width - contentMaxWidth) / 2);

  if (stepId === "agent" || stepId === "help") {
    return {
      height: 104,
      width: contentMaxWidth,
      x: contentLeft,
      y: 80,
    };
  }

  if (stepId === "navigation") {
    const navWidth = Math.min(930, contentMaxWidth);

    return {
      height: 60,
      width: navWidth,
      x: Math.max(0, (rootRect.width - navWidth) / 2),
      y: 184,
    };
  }

  return null;
}

function renderPatientLegacyOnboardingMask(input: {
  highlightRect: PatientOnboardingLayoutRect | null;
  layoutRects: PatientOnboardingLayoutRects;
  theme: AppShellTheme;
}) {
  const { highlightRect, layoutRects, theme } = input;
  const rootRect = layoutRects.root;
  const overlayOpacity = 0.88;

  if (!rootRect || !highlightRect) {
    return (
      <View
        style={[
          StyleSheet.absoluteFillObject,
          { backgroundColor: theme.surface, opacity: overlayOpacity },
        ]}
      />
    );
  }

  const highlightTop = Math.max(0, highlightRect.y);
  const highlightLeft = Math.max(0, highlightRect.x);
  const highlightRight = Math.min(
    rootRect.width,
    highlightRect.x + highlightRect.width,
  );
  const highlightBottom = Math.min(
    rootRect.height,
    highlightRect.y + highlightRect.height,
  );

  return (
    <>
      <View
        style={[
          styles.patientOnboardingMaskSegment,
          {
            backgroundColor: theme.surface,
            height: highlightTop,
            left: 0,
            opacity: overlayOpacity,
            top: 0,
            width: rootRect.width,
          },
        ]}
      />
      <View
        style={[
          styles.patientOnboardingMaskSegment,
          {
            backgroundColor: theme.surface,
            height: highlightBottom - highlightTop,
            left: 0,
            opacity: overlayOpacity,
            top: highlightTop,
            width: highlightLeft,
          },
        ]}
      />
      <View
        style={[
          styles.patientOnboardingMaskSegment,
          {
            backgroundColor: theme.surface,
            height: highlightBottom - highlightTop,
            left: highlightRight,
            opacity: overlayOpacity,
            top: highlightTop,
            width: Math.max(0, rootRect.width - highlightRight),
          },
        ]}
      />
      <View
        style={[
          styles.patientOnboardingMaskSegment,
          {
            borderColor: theme.accentStrong,
            borderRadius: 6,
            borderWidth: 2,
            height: highlightBottom - highlightTop,
            left: highlightLeft,
            top: highlightTop,
            width: highlightRight - highlightLeft,
          },
        ]}
      />
      <View
        style={[
          styles.patientOnboardingMaskSegment,
          {
            backgroundColor: theme.surface,
            height: Math.max(0, rootRect.height - highlightBottom),
            left: 0,
            opacity: overlayOpacity,
            top: highlightBottom,
            width: rootRect.width,
          },
        ]}
      />
    </>
  );
}

function resolvePatientLegacyOnboardingContentFrame(input: {
  highlightRect: PatientOnboardingLayoutRect | null;
  isFullStep: boolean;
  isMediumLayout: boolean;
  layoutRects: PatientOnboardingLayoutRects;
  stepId: PatientLegacyOnboardingStepId;
}) {
  const { highlightRect, isFullStep, isMediumLayout, layoutRects, stepId } =
    input;
  const rootRect = layoutRects.root;

  if (!rootRect || isFullStep || !isMediumLayout) {
    return StyleSheet.absoluteFillObject;
  }

  const mainTop = layoutRects.main?.y ?? 244;
  const heroNavTop = 184;
  const contentTop =
    stepId === "navigation"
      ? mainTop
      : stepId === "summary"
        ? 80
        : (highlightRect?.y ?? heroNavTop) + (highlightRect?.height ?? 0);

  return {
    bottom: 0,
    left: 0,
    position: "absolute" as const,
    right: 0,
    top: Math.min(Math.max(0, contentTop), rootRect.height),
  };
}

function renderPatientOnboardingControlButton(input: {
  disabled: boolean;
  labelText: string;
  onPress: () => void;
  primary?: boolean;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}) {
  const {
    disabled,
    labelText,
    onPress,
    primary = false,
    theme,
    tokens,
  } = input;

  return (
    <Pressable
      disabled={disabled}
      onPress={onPress}
      style={[
        styles.patientOnboardingControlButton,
        {
          backgroundColor: primary ? theme.accentStrong : theme.surface,
          borderColor: primary ? theme.accentStrong : theme.borderStrong,
          borderRadius: 6,
          opacity: disabled ? 0.62 : 1,
          paddingHorizontal: tokens.spacing.lg,
          paddingVertical: tokens.spacing.sm,
        },
      ]}
    >
      <Text
        style={[
          styles.patientOnboardingControlButtonText,
          {
            color: primary ? theme.surface : theme.textPrimary,
            fontSize: tokens.typography.fontSize.sm,
          },
        ]}
      >
        {labelText}
      </Text>
    </Pressable>
  );
}

export function renderPatientLegacyOnboardingOverlay(input: {
  flow: PatientLegacyOnboardingFlowState;
  isCommandSubmitting: boolean;
  isMediumLayout: boolean;
  layout: typeof designTokens.semanticLayout;
  layoutRects: PatientOnboardingLayoutRects;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
  theme: AppShellTheme;
  tokens: typeof designTokens;
}) {
  const {
    flow,
    isCommandSubmitting,
    isMediumLayout,
    layout,
    layoutRects,
    onBack,
    onNext,
    onSkip,
    theme,
    tokens,
  } = input;

  if (!flow.isActive) {
    return null;
  }

  const stepId = resolvePatientLegacyOnboardingStep(flow.stepIndex);
  const isFinalStep = flow.stepIndex >= patientLegacyOnboardingFinalStepIndex;
  const isFullStep = stepId === "welcome" || stepId === "introduction";
  const highlightRect = isFullStep
    ? null
    : resolvePatientLegacyOnboardingHighlightRect({
        isMediumLayout,
        layoutRects,
        stepId,
      });
  const contentFrame = resolvePatientLegacyOnboardingContentFrame({
    highlightRect,
    isFullStep,
    isMediumLayout,
    layoutRects,
    stepId,
  });

  return (
    <View style={styles.patientOnboardingOverlayRoot}>
      {renderPatientLegacyOnboardingMask({
        highlightRect,
        layoutRects,
        theme,
      })}
      <View
        style={[
          styles.patientOnboardingContentFrame,
          contentFrame,
          {
            padding: isMediumLayout ? tokens.spacing.xxl : tokens.spacing.lg,
          },
        ]}
      >
        <View
          style={[
            styles.patientOnboardingContentBox,
            {
              gap: tokens.spacing.md,
              maxWidth: isMediumLayout ? 960 : 720,
            },
          ]}
        >
          <View
            style={[
              styles.patientOnboardingStepBody,
              { gap: tokens.spacing.md },
            ]}
          >
            {renderPatientLegacyOnboardingStepContent({
              careType: flow.careType,
              isMediumLayout,
              layout,
              stepId,
              theme,
              tokens,
            })}
          </View>

          <View
            style={[styles.patientOnboardingFooter, { gap: tokens.spacing.xs }]}
          >
            <View
              style={[
                styles.patientOnboardingControls,
                { gap: tokens.spacing.sm },
              ]}
            >
              {flow.stepIndex > 0
                ? renderPatientOnboardingControlButton({
                    disabled: isCommandSubmitting,
                    labelText: "Back",
                    onPress: onBack,
                    theme,
                    tokens,
                  })
                : null}
              {renderPatientOnboardingControlButton({
                disabled: isCommandSubmitting,
                labelText: isFinalStep
                  ? isCommandSubmitting
                    ? "Finishing"
                    : "Finish"
                  : "Next",
                onPress: onNext,
                primary: true,
                theme,
                tokens,
              })}
            </View>

            <Pressable disabled={isCommandSubmitting} onPress={onSkip}>
              <Text
                style={[
                  styles.patientOnboardingSkipText,
                  {
                    color: theme.accentStrong,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                    opacity: isCommandSubmitting ? 0.6 : 1,
                  },
                ]}
              >
                Skip onboarding and go to Dashboard
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
}
